import React, { useState, useEffect } from 'react';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useTeamChat } from '@/context/TeamChatContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, X, LogOut, Star, Lock, Pencil, Upload, Search, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';


interface DetailsPanelProps {
  onClose: () => void;
  isGlobalDarkMode: boolean;
  isMobile?: boolean;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({ onClose, isGlobalDarkMode, isMobile = false }) => {
  const { userRole } = useWorkspace();
  const {
    activeChannelId,
    activeDmId,
    channels,
    directMessages,
    getDmParticipant,
    getUserById,
    updateChannel,
    leaveChannel,
    deleteChannel,
    currentUser,
    onlineUsers,
    isChannelMember
  } = useTeamChat();

  const activeChannel = channels.find(c => c.id === activeChannelId);
  const activeDm = directMessages.find(d => d.id === activeDmId);
  const dmParticipant = activeDm ? getDmParticipant(activeDm) : undefined;

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAvatarColor, setEditAvatarColor] = useState('');
  const [editAvatar, setEditAvatar] = useState<string | undefined>(undefined);
  const [editIsPrivate, setEditIsPrivate] = useState(false);

  useEffect(() => {
    if (activeChannel) {
      setEditName(activeChannel.name);
      setEditDescription(activeChannel.description || '');
      setEditAvatarColor(activeChannel.avatarColor || 'bg-slate-500');
      setEditAvatar(activeChannel.avatar);
      setEditIsPrivate(activeChannel.isPrivate || false);
    }
  }, [activeChannel]);


  const startEditing = () => {
    if (activeChannel) {
      setEditName(activeChannel.name);
      setEditDescription(activeChannel.description || '');
      setEditAvatarColor(activeChannel.avatarColor || 'bg-slate-500');
      setEditAvatar(activeChannel.avatar);
      setEditIsPrivate(activeChannel.isPrivate || false);
      setIsEditing(true);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChannel = () => {
    if (activeChannelId) {
      updateChannel(activeChannelId, {
        name: editName,
        description: editDescription,
        avatarColor: editAvatarColor,
        avatar: editAvatar,
        isPrivate: editIsPrivate
      });
      setIsEditing(false);
    }
  };

  const handleLeaveChat = async () => {
    if (activeChannelId && confirm("Are you sure you want to leave this channel?")) {
      await leaveChannel(activeChannelId);
      onClose();
    }
  };

  const handleDeleteChat = async () => {
    if (activeChannelId && confirm("Are you sure you want to delete this group chat? This action cannot be undone.")) {
      await deleteChannel(activeChannelId);
      onClose();
    }
  };

  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [isAllMembersVisible, setIsAllMembersVisible] = useState(false);


  // Get channel members
  const channelMembers = activeChannel
    ? activeChannel.members
      .map(id => getUserById(id))
      .filter(Boolean)
      .filter(member =>
        member?.displayName.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
        member?.name.toLowerCase().includes(memberSearchQuery.toLowerCase())
      )
    : [];

  const displayedMembers = isAllMembersVisible || memberSearchQuery ? channelMembers : channelMembers.slice(0, 4);

  const bgColor = isGlobalDarkMode ? 'bg-sidebar' : 'bg-white';
  const borderColor = isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200';
  const textColor = isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-600';
  const headerBg = isGlobalDarkMode ? 'bg-sidebar/50' : 'bg-white';

  // Check if user is a member of the channel
  const isMember = activeChannelId ? isChannelMember(activeChannelId) : false;

  // Check permissions: Admin, Manager, or Creator (and must be a member)
  const canEdit = activeChannel && isMember && (
    ['owner', 'admin', 'manager'].includes(userRole || '') ||
    activeChannel.createdBy === currentUser?.id
  );

  return (
    <div className={cn("flex flex-col h-full", isMobile ? "w-full" : "w-72 border-l", bgColor, borderColor)}>
      {/* Header */}
      <div className={cn("h-12 pl-4 pr-6 md:pr-10 flex items-center justify-between border-b backdrop-blur-sm flex-shrink-0 z-10", headerBg, borderColor)}>
        <div className="flex items-center gap-2">
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className={cn(
                "p-2 h-8 w-8 flex-shrink-0",
                isGlobalDarkMode
                  ? "hover:bg-card text-muted-foreground hover:text-foreground"
                  : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              )}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <span className={cn("font-bold text-sm", textColor)}>Details</span>
        </div>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-card text-muted-foreground" : "hover:bg-gray-100 text-gray-500")}
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="pl-4 pr-6 md:pr-10 py-4">
          {/* Group/Channel Info */}
          {activeChannel && (
            <>
              {/* Group Profile */}
              <div className="flex flex-col items-center text-center mb-6 relative">
                {isEditing ? (
                  <div className="w-full space-y-4 px-2">
                    <div className="flex justify-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className={cn("w-32 h-32 rounded-full flex items-center justify-center text-white text-3xl font-bold overflow-hidden relative group", editAvatarColor)}>
                          {editAvatar ? (
                            <img src={editAvatar} alt="Group Avatar" className="w-full h-full object-cover" />
                          ) : (
                            editName.charAt(0).toUpperCase()
                          )}

                          <label
                            htmlFor="avatar-upload"
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <Upload className="w-6 h-6 text-white" />
                          </label>
                          <input
                            id="avatar-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageUpload}
                          />
                        </div>

                        {editAvatar && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setEditAvatar(undefined)}
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-3 pt-2">
                      <div className="space-y-1 text-left">
                        <Label htmlFor="group-name" className={subTextColor}>Name</Label>
                        <Input
                          id="group-name"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className={cn(isGlobalDarkMode && "bg-card border-border text-foreground")}
                        />
                      </div>
                      <div className="space-y-1 text-left">
                        <Label htmlFor="group-desc" className={subTextColor}>Description</Label>
                        <Input
                          id="group-desc"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className={cn(isGlobalDarkMode && "bg-card border-border text-foreground")}
                        />
                      </div>
                      <div className="flex items-center space-x-2 pt-2">
                        <Switch
                          id="group-private"
                          checked={editIsPrivate}
                          onCheckedChange={setEditIsPrivate}
                          className={cn(isGlobalDarkMode && "data-[state=checked]:bg-workspace-accent")}
                        />
                        <Label htmlFor="group-private" className={cn("text-xs", subTextColor)}>Private Group</Label>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                        className={cn("transition-colors", isGlobalDarkMode ? "border-border hover:bg-card text-muted-foreground" : "border-gray-200 hover:bg-gray-100 text-gray-600")}
                      >
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveChannel} className="bg-workspace-accent hover:bg-workspace-accent-hover text-white">Save</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={cn("w-24 h-24 rounded-full flex items-center justify-center mb-3 text-white text-3xl font-bold overflow-hidden shadow-lg", activeChannel.avatarColor || "bg-slate-500 shadow-slate-500/20")}>
                      {activeChannel.avatar ? (
                        <img src={activeChannel.avatar} alt={activeChannel.name} className="w-full h-full object-cover" />
                      ) : (
                        activeChannel.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <h3 className={cn("font-bold text-lg", textColor)}>{activeChannel.name}</h3>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("h-7 w-7 transition-colors", isGlobalDarkMode ? "text-muted-foreground hover:text-foreground hover:bg-card" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100")}
                          onClick={startEditing}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {activeChannel.isPrivate && (
                      <div className={cn("flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider mt-1", isGlobalDarkMode ? "text-workspace-accent/60" : "text-primary/60")}>
                        <Lock className="w-3 h-3" />
                        Private
                      </div>
                    )}
                    {activeChannel.description && (
                      <p className={cn("text-sm mt-2 px-2 leading-relaxed opacity-70", textColor)}>{activeChannel.description}</p>
                    )}
                  </>
                )}
              </div>

              {/* Members List */}
              <div className="mb-4">
                <h4 className={cn("text-[10px] font-bold uppercase tracking-wider mb-3", subTextColor)}>
                  Members ({channelMembers.length})
                </h4>

                <div className="relative mb-4">
                  <Search className={cn("absolute left-2.5 top-2.5 h-3.5 w-3.5 transition-colors", isGlobalDarkMode ? "text-gray-500" : "text-gray-400")} />
                  <Input
                    placeholder="Find members"
                    className={cn(
                      "pl-8 h-8 text-xs border-0 transition-all",
                      isGlobalDarkMode
                        ? "bg-card text-foreground placeholder:text-muted-foreground focus:bg-accent"
                        : "bg-gray-100 text-gray-900 placeholder:text-gray-400 focus:bg-gray-200"
                    )}
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  {displayedMembers.map((member, index) => (
                    <div key={member?.id || index} className={cn("flex items-center gap-3 p-2 rounded-lg transition-colors", isGlobalDarkMode ? "hover:bg-card/50" : "hover:bg-muted/50")}>
                      <div className="relative">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={member?.avatar} alt={member?.displayName} />
                          <AvatarFallback className={cn("text-xs font-semibold", isGlobalDarkMode ? "bg-card text-muted-foreground" : "bg-primary/10 text-primary")}>
                            {member?.displayName?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        {/* Online presence dot */}
                        <span className={cn(
                          "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2",
                          isGlobalDarkMode ? "border-background" : "border-background",
                          member && onlineUsers.includes(member.id) ? 'bg-green-500' : 'bg-gray-400'
                        )} />
                      </div>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={cn("text-sm font-medium truncate", textColor)}>
                          {member?.id === currentUser?.id ? 'You' : member?.displayName}
                        </span>
                        {activeChannel?.members[0] === member?.id && (
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}

                  {channelMembers.length === 0 && (
                    <p className={cn("text-xs text-center py-4", subTextColor)}>No members found</p>
                  )}

                  {activeChannel?.members.length > 4 && !memberSearchQuery && (
                    <Button
                      variant="ghost"
                      className={cn("w-full text-xs h-8 mt-2", isGlobalDarkMode ? "text-muted-foreground hover:bg-card hover:text-foreground" : "text-muted-foreground")}
                      onClick={() => setIsAllMembersVisible(!isAllMembersVisible)}
                    >
                      {isAllMembersVisible ? "Show less" : "View all members"}
                    </Button>
                  )}
                </div>
              </div>

            </>
          )}

          {/* DM Info */}
          {activeDm && dmParticipant && (
            <>
              {/* User Profile */}
              <div className="flex flex-col items-center text-center mb-6">
                <Avatar className="w-20 h-20 mb-3 border-4 border-[var(--workspace-bg,#FFFFFF)] shadow-lg">
                  <AvatarImage src={dmParticipant.avatar} alt={dmParticipant.displayName} />
                  <AvatarFallback className={cn("text-2xl font-bold", isGlobalDarkMode ? "bg-card text-muted-foreground" : "bg-primary/10 text-primary")}>
                    {dmParticipant.displayName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <h3 className={cn("font-bold text-lg", textColor)}>{dmParticipant.displayName}</h3>
                <p className={cn("text-sm", subTextColor)}>@{dmParticipant.name}</p>
                <div className="flex items-center gap-2 mt-3 px-3 py-1 rounded-full bg-gray-500/10">
                  <span className={cn(
                    "w-2 h-2 rounded-full",
                    onlineUsers.includes(dmParticipant.id) ? 'bg-emerald-500' : 'bg-gray-400'
                  )} />
                  <span className={cn("text-[10px] font-bold uppercase tracking-wider", subTextColor)}>
                    {onlineUsers.includes(dmParticipant.id) ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              {/* User Details */}
              <div className={cn("space-y-4 border-t pt-6", borderColor)}>
                <div>
                  <h4 className={cn("text-[10px] font-bold uppercase tracking-wider mb-1", subTextColor)}>Display Name</h4>
                  <p className={cn("text-sm", textColor)}>{dmParticipant.displayName}</p>
                </div>
                <div>
                  <h4 className={cn("text-[10px] font-bold uppercase tracking-wider mb-1", subTextColor)}>Username</h4>
                  <p className={cn("text-sm", textColor)}>@{dmParticipant.name}</p>
                </div>
              </div>

            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer with Leave Chat and Delete Chat buttons - Only for members */}
      {isMember && (
        <div className={cn("pl-4 pr-6 md:pr-10 py-4 border-t mt-auto space-y-2", borderColor)}>
          <Button
            variant="outline"
            onClick={handleLeaveChat}
            className={cn("w-full justify-center gap-2", isGlobalDarkMode ? "bg-transparent hover:bg-card text-muted-foreground" : "text-gray-600")}
          >
            <LogOut className="w-4 h-4" />
            Leave chat
          </Button>

          {canEdit && activeChannel && (
            <Button
              variant="outline"
              onClick={handleDeleteChat}
              className={cn("w-full justify-center gap-2 border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-600", isGlobalDarkMode && "bg-transparent")}
            >
              <Trash2 className="w-4 h-4" />
              Delete Group Chat
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsPanel;
